import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import type { RequirementFinding } from "../api/contentRegistryApi";
import { REQUIREMENT_PAGE_SIZE, type RequirementTabKey } from "../constants";
import { SectionCard } from "../Domain";
import { EmptyRow, RequirementRow } from "./requirementIntelligenceParts";
import { RequirementPagination } from "./requirementPagination";
import {
  REQUIREMENT_LIST_COPY,
  REQUIREMENT_TAB_KEYS,
  REQUIREMENT_TAB_LABEL_PREFIX,
  REQ_TAB_TRIGGER_CLS,
  requirementRowKey,
} from "./utilities";

export interface RequirementsListSectionProps {
  findings: RequirementFinding[];
  filteredFindings: RequirementFinding[];
  mandatoryCount: number;
  prohibitedCount: number;
  flaggedCount: number;
  tab: RequirementTabKey;
  onTabChange: (tab: RequirementTabKey) => void;
  page: number;
  onPageChange: (page: number) => void;
  expanded: Set<string>;
  onToggleExpand: (key: string) => void;
}

interface TabDef {
  key: RequirementTabKey;
  label: string;
}

function buildTabDefs(counts: {
  all: number;
  mandatory: number;
  prohibited: number;
  flagged: number;
}): TabDef[] {
  return REQUIREMENT_TAB_KEYS.map((key) => ({
    key,
    label: `${REQUIREMENT_TAB_LABEL_PREFIX[key]} ${counts[key]}`,
  }));
}

export function RequirementsListSection({
  findings,
  filteredFindings,
  mandatoryCount,
  prohibitedCount,
  flaggedCount,
  tab,
  onTabChange,
  page,
  onPageChange,
  expanded,
  onToggleExpand,
}: RequirementsListSectionProps) {
  const totalPages = Math.max(
    1,
    Math.ceil(filteredFindings.length / REQUIREMENT_PAGE_SIZE),
  );
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageStart = (safePage - 1) * REQUIREMENT_PAGE_SIZE;
  const visibleFindings = filteredFindings.slice(
    pageStart,
    pageStart + REQUIREMENT_PAGE_SIZE,
  );
  const rangeStart = filteredFindings.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = pageStart + visibleFindings.length;

  const tabs = buildTabDefs({
    all: findings.length,
    mandatory: mandatoryCount,
    prohibited: prohibitedCount,
    flagged: flaggedCount,
  });

  return (
    <SectionCard
      headerLabel={REQUIREMENT_LIST_COPY.header}
      rightLabel={`${findings.length} total`}
    >
      <div className="flex flex-col gap-3 p-3">
        <Tabs
          value={tab}
          onValueChange={(value) => onTabChange(value as RequirementTabKey)}
          className="gap-0"
        >
          <TabsList className="h-auto flex-wrap justify-start gap-1.5 bg-transparent p-0">
            {tabs.map((item) => (
              <TabsTrigger
                key={item.key}
                value={item.key}
                className={REQ_TAB_TRIGGER_CLS}
              >
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {visibleFindings.length === 0 ? (
          <EmptyRow text={REQUIREMENT_LIST_COPY.emptyText} />
        ) : (
          <ul className="flex flex-col gap-2">
            {visibleFindings.map((finding, index) => {
              const rowKey = requirementRowKey(finding, pageStart + index);
              return (
                <RequirementRow
                  key={rowKey}
                  finding={finding}
                  expanded={expanded.has(rowKey)}
                  onToggle={() => onToggleExpand(rowKey)}
                />
              );
            })}
          </ul>
        )}

        {filteredFindings.length > 0 && (
          <RequirementPagination
            page={safePage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalFiltered={filteredFindings.length}
          />
        )}
      </div>
    </SectionCard>
  );
}
